/*
 * Copyright (c) 2025. Jellybean B.V.
 * Jellybean B.V. Apeldoorn heeft een licentie verleend aan
 * Spin in het Web B.V. Apeldoorn voor het gebruik van deze software-
 * onderdelen in projecten voor derden. U heeft géén toestemming
 * tot inzage of wijziging in de (bron)code van deze onderdelen.
 */

/*
* sihwdatumveld.ts
* 17-11-2021 - Jelmer Jellema - Spin in het Web B.V.
*/

import {Component, EventEmitter, forwardRef, HostBinding, Input, Output} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator
} from '@angular/forms';
import {Sihwlog, Sihwlogger} from '../../sihwlog/sihwlog';
import moment from 'moment';

@Component({
  selector: 'sihwdatumveld',
  templateUrl: './sihwdatumveld.html',
  styleUrls: ['./sihwdatumveld.scss'],
  providers: [
    {provide: NG_VALUE_ACCESSOR, useExisting: Sihwdatumveld, multi: true},
    {provide: NG_VALIDATORS, useExisting: forwardRef(() => Sihwdatumveld), multi: true} //doorgeven valid state
  ],
  standalone: false
})
export class Sihwdatumveld implements ControlValueAccessor, Validator {
  static naamteller:number = 0;
  private log: Sihwlogger;
  ctrlval: string | null = null; //onze ngmodel var
  private value: string | null = null;
  private valid: boolean = true; //vinden wij het valid
  private verandering: boolean = false; //is er een change geweest? Gebruikt bij blur
  private meldGewijzigd: (_: any) => void = (_) => {
  }; //controlvalueaccessor
  onTouched: () => void = () => {
  }; //controlvalueaccessor
  naam!: string;

  @Input('disabled') disabled: boolean = false; //de disabled value, en readonly op de host. Dat gebruiken we als alternatief voor disabled, wat we niet goed kunnen zetten
  @HostBinding('attr.readonly') get hostReadonly() {
    return this.disabled ? true : null;
  } //zorg voor verwijderen readonly als niet nodig

  //copy props
  @Input() placeholder: string = '';
  @Input() size: Number = 5; //er moet een default zijn
  @Input() format: string = "DD-MM-YYYY"; //wweergave en parsen
  @Input('control-class') controlClass: string = ""; //zo kan je dus een class op de interne control zetten

  @Output('focus') focusEvent: EventEmitter<FocusEvent> = new EventEmitter();
  constructor(sihwlog: Sihwlog) {
    this.log = sihwlog.logger();
    Sihwdatumveld.naamteller++;
    this.naam = "sihwdatumveld" + Sihwdatumveld.naamteller;
  }

  ///////////////////////////////////////// CONTROLVALUEACCESSOR //////////////////
  /**
   * Nieuwe waarde wordt aangeboden door ngmodel enzo
   * @param obj
   */
  writeValue(obj: any): void {
    //de waarde komt als string binnen, in ons interne format
    this.log.debug(`writeValue`,obj);
    //voor de zekerheid
    if (obj === null)
    {
      this.value = null;
      this.valid = true;
      this.ctrlval = null;
    }
    else{
      let m = moment(obj); //moet in YYYY-MM-DD zijn
      if (m.isValid())
      {
        this.value = m.format('YYYY-MM-DD'); //eventueel opschonen
        this.valid = true;
        this.ctrlval = this.toString();
      }
      else {
        this.ctrlval = obj; //dan maar letterlijk
        this.value = null;
        this.valid = false;
      }
    }
  }

  registerOnChange(fn: any): void {
    this.meldGewijzigd = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.log.debug(`sihwdatumveld setDisabled`, isDisabled);
    this.disabled = isDisabled;
  }

  /////////////////////////////// Validator ////////////////////////////////
  validate(control: AbstractControl): ValidationErrors | null {
    //dit hebben we al gedaan
    this.log.debug(`sihwdatumveld validate`, this.valid);
    return this.valid ? null : {sihwdatumveld: true};
  }

  //////////////////////////////// MODEL //////////////////////////////////////////

  /**
   * 1. Binnenkomende waarde: zet de juiste stringrepresentatie in ons veld. That's all. Blijf van de waarde af
   * 2. Gewijzigde waarde: parse en bewaar de juiste stringrepresentatie. Stuur hem naar boven
   * 3. Blur  / Enter: zet de juiste stringrepresentatie als er gewijzigd is
   */

  /**
   * ngOnChange
   */
  valueChanged() {
    //opnieuw parsen
    this.log.debug(`nieuwe waarde ingevuld`);
    this.parseCtrlVal(); //zet this.value
    //we melden geen invalid waarden
    this.meldGewijzigd(this.valid ? this.value : null);
    this.verandering = true; //zie ctrlBlur
  }

  /**
   * Blur: werk de waarde bij en ga door
   */
  ctrlBlur() {
    this.werkCtrlValBij(); //weergave
    //sowieso touched
    this.onTouched();
  }

  //enterkey: ook de display waarde bijwerken
  enterkey(event: Event) {
    this.werkCtrlValBij();
    //en niet door
    event.preventDefault();
    event.stopPropagation();
  }

  /**
   * Helper bij blur en enterkey
   */
  werkCtrlValBij() {
    if (this.verandering && this.valid) {
      this.ctrlval = this.toString(); //mooi format
    }
    this.verandering = false;
  }

  /////////////////////////////////////////////////////////////////////////////////
  /**
   * Helper: zet this.value op uit de ctrlval, en zet this.valid
   */
  parseCtrlVal() {

    if (!(this.ctrlval && this.ctrlval.length)) {
      this.valid = true; //we vinden het prima. Required/ empty validators gaan apart
      this.value = null;
      return;
    }
    let m = moment(this.ctrlval, this.format);
    if (m.isValid())
    {
      //geldig
      this.value = m.format('YYYY-MM-DD'); //altijd
      this.valid = true;
    }
    else {
      this.value = null;
      this.valid = false;
    }
    this.log.debug(`parseVal`, this.ctrlval, this.value);
  }

  /**
   * Zet onze interne tijdrepresentatie om naar een string of null bij null
   */
  private toString(): string | null {
    if (this.value === null) {
      return null;
    }
    return moment(this.value).format(this.format);
  }

  /**
   * Stuur de focus van de control door als onze focus
   */
  ctrlFocus(e: FocusEvent) {
    this.focusEvent.emit(e);
  }
}
